import { PageLayout } from '@/layouts/page-layout'
import { useAuthorization, useFileUploadPolicy } from '@/hooks'
import { Pagination } from '@/shared/components/pagination'
import { fileToAttachment } from '@/utils/file-to-attachment'
import {
  useCreateReferenceLibraryFolderMutation,
  useCreateReferenceLibraryItemMutation,
  useDeleteReferenceLibraryFolderMutation,
  useDeleteReferenceLibraryItemMutation,
  useGetReferenceLibraryFoldersQuery,
  useGetReferenceLibraryItemsQuery,
  useUpdateReferenceLibraryFolderMutation,
  useUpdateReferenceLibraryItemMutation
} from '@/store/services'
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader, Input, Textarea, Tooltip, Typography } from '@material-tailwind/react'
import { usePagination } from '@table-library/react-table-library/pagination'
import {
  Body,
  Cell,
  Header,
  HeaderCell,
  HeaderRow,
  Row,
  Table
} from '@table-library/react-table-library/table'
import { useTheme } from '@table-library/react-table-library/theme'
import { useEffect, useMemo, useState } from 'react'
import { FiDownload, FiEdit2, FiExternalLink, FiFileText, FiFolder, FiInfo, FiPlus, FiTrash2, FiX } from 'react-icons/fi'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

const emptyItemForm = {
  title: '',
  description: '',
  external_url: '',
  reference_library_folder_id: '',
  document: {}
}

const emptyFolderForm = {
  name: '',
  description: ''
}

const roleNamesFor = (currentUser = {}) => {
  const user = currentUser || {}
  const roleNames = (user.roles || []).map((role) => role?.name)
  const camelRoleNames = Array.isArray(user.roleNames) ? user.roleNames : []
  const snakeRoleNames = Array.isArray(user.role_names) ? user.role_names : []

  return [...roleNames, ...camelRoleNames, ...snakeRoleNames]
    .filter(Boolean)
    .map((name) => String(name).toLowerCase())
}

const isAdminUser = (currentUser = {}) => roleNamesFor(currentUser).includes('admin')

const selectedFolderLabel = (folderId, folders = []) => {
  if (!folderId) return 'Todas las carpetas'

  return folders.find((folder) => String(folder.id) === String(folderId))?.name || 'Carpeta'
}

const itemDocumentUrl = (item) => item?.documentInfo?.url || item?.document_info?.url
const itemDocumentFilename = (item) => item?.documentInfo?.filename || item?.document_info?.filename
const itemExternalUrl = (item) => item.externalUrl || item.external_url
const truncateText = (text = '', maxLength = 180) => {
  if (!text) return 'Sin descripción'
  if (text.length <= maxLength) return text

  return `${text.slice(0, maxLength).trim()}...`
}

export const ReferenceLibraryList = () => {
  const { hasAccess } = useAuthorization({ redirection: { enable: true } })
  const currentUser = useSelector((state) => state.user.current)
  const { maxFileSizeLabel, validateFileSize } = useFileUploadPolicy()
  const canManage = isAdminUser(currentUser)
  const [searchParams, setSearchParams] = useSearchParams()
  const [itemFormOpen, setItemFormOpen] = useState(false)
  const [folderFormOpen, setFolderFormOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [editingFolder, setEditingFolder] = useState(null)
  const [detailsItem, setDetailsItem] = useState(null)
  const [itemForm, setItemForm] = useState(emptyItemForm)
  const [folderForm, setFolderForm] = useState(emptyFolderForm)
  const [formError, setFormError] = useState('')

  useEffect(() => {
    hasAccess('reference_library#list')
  }, [])

  useEffect(() => {
    if (!searchParams.get('page') || !searchParams.get('per_page')) {
      const next = new URLSearchParams(searchParams)
      if (!next.get('page')) next.set('page', 1)
      if (!next.get('per_page')) next.set('per_page', 10)
      setSearchParams(next)
    }
  }, [searchParams, setSearchParams])

  const queryString = useMemo(() => `?${searchParams.toString()}`, [searchParams])
  const { data: itemsResponse = {}, isFetching } = useGetReferenceLibraryItemsQuery(queryString, {
    skip: !searchParams.get('page') || !searchParams.get('per_page')
  })
  const { data: foldersResponse = {} } = useGetReferenceLibraryFoldersQuery()
  const [createItem, { isLoading: isCreatingItem }] = useCreateReferenceLibraryItemMutation()
  const [updateItem, { isLoading: isUpdatingItem }] = useUpdateReferenceLibraryItemMutation()
  const [deleteItem] = useDeleteReferenceLibraryItemMutation()
  const [createFolder, { isLoading: isCreatingFolder }] = useCreateReferenceLibraryFolderMutation()
  const [updateFolder, { isLoading: isUpdatingFolder }] = useUpdateReferenceLibraryFolderMutation()
  const [deleteFolder] = useDeleteReferenceLibraryFolderMutation()

  const items = itemsResponse.referenceLibraryItems || itemsResponse.reference_library_items || []
  const folders = foldersResponse.referenceLibraryFolders || foldersResponse.reference_library_folders || []
  const meta = itemsResponse.meta || {}
  const totalPages = meta?.pagination?.total_pages || 0
  const selectedFolderId = searchParams.get('folder_id') || ''
  const showFolderColumn = !selectedFolderId
  const hasItems = items.length > 0
  const nodes = { nodes: items }

  const pagination = usePagination(
    nodes,
    {
      state: {
        page: Number(searchParams.get('page') || 1) - 1,
        size: Number(searchParams.get('per_page') || 10)
      },
      onChange: (_, state) => {
        const next = new URLSearchParams(searchParams)
        next.set('page', state.page + 1)
        next.set('per_page', searchParams.get('per_page') || 10)
        setSearchParams(next)
      }
    },
    { isServer: true }
  )

  const theme = useTheme({
    Table: `
      --data-table-library_grid-template-columns: ${showFolderColumn ? 'minmax(0, 1fr) 180px 72px 120px' : 'minmax(0, 1fr) 72px 120px'};
      min-width: 0;
    `,
    BaseCell: `
      padding-left: 12px;
      padding-right: 12px;
      &:nth-of-type(${showFolderColumn ? 4 : 3}) {
        right: 0px;
      }
    `
  })

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set(key, value)
    } else {
      next.delete(key)
    }
    next.set('page', 1)
    if (!next.get('per_page')) next.set('per_page', 10)
    setSearchParams(next)
  }

  const resetItemForm = () => {
    setEditingItem(null)
    setItemForm(emptyItemForm)
    setItemFormOpen(false)
    setFormError('')
  }

  const resetFolderForm = () => {
    setEditingFolder(null)
    setFolderForm(emptyFolderForm)
    setFolderFormOpen(false)
  }

  const openItemForm = (item = null) => {
    setEditingItem(item)
    setItemForm({
      title: item?.title || '',
      description: item?.description || '',
      external_url: item?.externalUrl || item?.external_url || '',
      reference_library_folder_id: item?.referenceLibraryFolderId || item?.reference_library_folder_id || '',
      document: {}
    })
    setFormError('')
    setItemFormOpen(true)
  }

  const openFolderForm = (folder = null) => {
    setEditingFolder(folder)
    setFolderForm({
      name: folder?.name || '',
      description: folder?.description || ''
    })
    setFolderFormOpen(true)
  }

  const handleFile = (event) => {
    const [file] = Array.from(event.target.files || [])
    if (!file) return

    const sizeError = validateFileSize(file)
    if (sizeError) {
      setFormError(sizeError)
      event.target.value = ''
      return
    }

    setFormError('')
    fileToAttachment(file, (document) => {
      setItemForm((previous) => ({ ...previous, document }))
    })
  }

  const submitItem = async (event) => {
    event.preventDefault()
    setFormError('')

    const hasExistingDocument = Boolean(editingItem && itemDocumentUrl(editingItem))
    if (!itemForm.title.trim()) {
      setFormError('Ingresa un nombre para el documento')
      return
    }
    if (!itemForm.document?.filename && !itemForm.external_url.trim() && !hasExistingDocument) {
      setFormError('Sube un archivo o indica un enlace')
      return
    }

    const payload = {
      ...itemForm,
      reference_library_folder_id: itemForm.reference_library_folder_id || null,
      external_url: itemForm.external_url.trim() || null
    }

    if (!payload.document?.filename) delete payload.document

    if (editingItem) {
      await updateItem({ id: editingItem.id, ...payload }).unwrap()
    } else {
      await createItem(payload).unwrap()
    }

    resetItemForm()
  }

  const submitFolder = async (event) => {
    event.preventDefault()
    if (!folderForm.name.trim()) return

    if (editingFolder) {
      await updateFolder({ id: editingFolder.id, ...folderForm }).unwrap()
    } else {
      await createFolder(folderForm).unwrap()
    }

    resetFolderForm()
  }

  const removeItem = async (item) => {
    if (!window.confirm(`Eliminar ${item.title}?`)) return

    await deleteItem(item.id).unwrap()
  }

  const removeFolder = async (folder) => {
    if (Number(folder.itemsCount || folder.items_count || 0) > 0) return
    if (!window.confirm(`Eliminar la carpeta ${folder.name}?`)) return

    await deleteFolder(folder.id).unwrap()
  }

  const detailsDocumentUrl = detailsItem ? itemDocumentUrl(detailsItem) : null
  const detailsFilename = detailsItem ? itemDocumentFilename(detailsItem) : null
  const detailsExternalUrl = detailsItem ? itemExternalUrl(detailsItem) : null

  return (
    <>
      <Dialog open={Boolean(detailsItem)} handler={() => setDetailsItem(null)} size='md'>
        <DialogHeader>{detailsItem?.title}</DialogHeader>
        <DialogBody className='grid gap-4'>
          <div>
            <Typography variant='small' className='font-semibold text-gray-700'>Carpeta</Typography>
            <Typography variant='small'>{detailsItem?.folder?.name || 'Sin carpeta'}</Typography>
          </div>
          <div>
            <Typography variant='small' className='font-semibold text-gray-700'>Descripción</Typography>
            <Typography variant='small' className='whitespace-pre-wrap'>
              {detailsItem?.description || 'Sin descripción'}
            </Typography>
          </div>
          {detailsFilename && (
            <div>
              <Typography variant='small' className='font-semibold text-gray-700'>Archivo</Typography>
              <a href={detailsDocumentUrl} target='_blank' rel='noreferrer' className='text-sm text-blue-600 break-all'>
                {detailsFilename}
              </a>
            </div>
          )}
          {detailsExternalUrl && (
            <div>
              <Typography variant='small' className='font-semibold text-gray-700'>Enlace</Typography>
              <a href={detailsExternalUrl} target='_blank' rel='noreferrer' className='text-sm text-blue-600 break-all'>
                {detailsExternalUrl}
              </a>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type='button' variant='text' onClick={() => setDetailsItem(null)}>Cerrar</Button>
        </DialogFooter>
      </Dialog>

      <PageLayout
        title='Biblioteca de referencia'
        description='Reglamentos, manuales, formularios y enlaces vigentes'
        actions={[
          <div key='reference-library-actions' className='flex flex-wrap items-center justify-end gap-3'>
            <Input
              name='q'
              label='Buscar'
              value={searchParams.get('q') || ''}
              onChange={(event) => setParam('q', event.target.value)}
            />
            {canManage && (
              <>
                <Button
                  type='button'
                  variant='outlined'
                  color='white'
                  className='flex items-center gap-2 text-nowrap'
                  onClick={() => openFolderForm()}
                >
                  <FiFolder />
                  Carpeta
                </Button>
                <Button
                  type='button'
                  className='flex items-center gap-2 text-nowrap'
                  onClick={() => openItemForm()}
                >
                  <FiPlus />
                  Documento
                </Button>
              </>
            )}
          </div>
        ]}
      >
        <div className='grid grid-cols-1 xl:grid-cols-[260px_1fr] gap-6'>
        <aside className='border border-gray-200 bg-white p-4 h-max'>
          <div className='flex items-center justify-between mb-4'>
            <Typography variant='h6'>Carpetas</Typography>
            {selectedFolderId && (
              <button
                type='button'
                className='text-sm text-blue-600'
                onClick={() => setParam('folder_id', '')}
              >
                Ver todo
              </button>
            )}
          </div>

          <button
            type='button'
            className={`w-full text-left px-3 py-2 text-sm border ${!selectedFolderId ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent text-gray-700'}`}
            onClick={() => setParam('folder_id', '')}
          >
            Todas las carpetas
          </button>

          <div className='mt-2 grid gap-1'>
            {folders.map((folder) => (
              <div key={folder.id} className={`grid items-start gap-2 ${canManage ? 'grid-cols-[minmax(0,1fr)_auto]' : 'grid-cols-1'}`}>
                <button
                  type='button'
                  className={`min-w-0 text-left px-3 py-2 text-sm border ${String(selectedFolderId) === String(folder.id) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent text-gray-700'}`}
                  onClick={() => setParam('folder_id', folder.id)}
                >
                  <span className='block font-medium whitespace-normal break-words leading-5' style={{ overflowWrap: 'anywhere' }}>
                    {folder.name}
                  </span>
                  <span className='block text-xs text-gray-500'>{folder.itemsCount || folder.items_count || 0} ítems</span>
                </button>
                {canManage && (
                  <div className='flex shrink-0 items-center gap-2 pt-2'>
                    <button type='button' aria-label='Editar carpeta' onClick={() => openFolderForm(folder)}>
                      <FiEdit2 />
                    </button>
                    {Number(folder.itemsCount || folder.items_count || 0) === 0 && (
                      <button type='button' aria-label='Eliminar carpeta' onClick={() => removeFolder(folder)}>
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        <section className='grid gap-5 min-w-0'>
          {(itemFormOpen || folderFormOpen) && canManage && (
            <div className='border border-gray-200 bg-white p-5'>
              <div className='flex justify-between items-start gap-4 mb-5'>
                <div>
                  <Typography variant='h6'>
                    {itemFormOpen
                      ? editingItem ? 'Editar documento' : 'Nuevo documento'
                      : editingFolder ? 'Editar carpeta' : 'Nueva carpeta'}
                  </Typography>
                  <Typography variant='small' className='text-gray-500'>
                    {itemFormOpen ? selectedFolderLabel(itemForm.reference_library_folder_id, folders) : 'Organiza documentos por tema'}
                  </Typography>
                </div>
                <button
                  type='button'
                  aria-label='Cerrar formulario'
                  onClick={itemFormOpen ? resetItemForm : resetFolderForm}
                >
                  <FiX />
                </button>
              </div>

              {itemFormOpen && (
                <form className='grid gap-4' onSubmit={submitItem}>
                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                    <Input
                      required
                      label='Nombre'
                      value={itemForm.title}
                      onChange={(event) => setItemForm((previous) => ({ ...previous, title: event.target.value }))}
                    />
                    <select
                      className='border border-gray-300 px-3 py-2 text-sm text-gray-700'
                      value={itemForm.reference_library_folder_id || ''}
                      onChange={(event) => setItemForm((previous) => ({ ...previous, reference_library_folder_id: event.target.value }))}
                    >
                      <option value=''>Sin carpeta</option>
                      {folders.map((folder) => (
                        <option key={folder.id} value={folder.id}>{folder.name}</option>
                      ))}
                    </select>
                  </div>
                  <Textarea
                    label='Descripción'
                    value={itemForm.description}
                    onChange={(event) => setItemForm((previous) => ({ ...previous, description: event.target.value }))}
                  />
                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                    <Input
                      label='Enlace'
                      value={itemForm.external_url}
                      onChange={(event) => setItemForm((previous) => ({ ...previous, external_url: event.target.value }))}
                    />
                    <label className='border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-600 cursor-pointer'>
                      <input type='file' className='hidden' onChange={handleFile} />
                      <span className='block'>{itemForm.document?.filename || itemDocumentFilename(editingItem) || 'Subir archivo'}</span>
                      <span className='mt-1 block text-xs text-gray-500'>Tamaño máximo por archivo: {maxFileSizeLabel}.</span>
                    </label>
                  </div>
                  {formError && <p className='text-sm text-red-600'>{formError}</p>}
                  <div className='flex justify-end gap-3'>
                    <Button type='button' variant='text' onClick={resetItemForm}>Cancelar</Button>
                    <Button type='submit' loading={isCreatingItem || isUpdatingItem}>Guardar</Button>
                  </div>
                </form>
              )}

              {folderFormOpen && (
                <form className='grid gap-4' onSubmit={submitFolder}>
                  <Input
                    required
                    label='Nombre'
                    value={folderForm.name}
                    onChange={(event) => setFolderForm((previous) => ({ ...previous, name: event.target.value }))}
                  />
                  <Textarea
                    label='Descripción'
                    value={folderForm.description}
                    onChange={(event) => setFolderForm((previous) => ({ ...previous, description: event.target.value }))}
                  />
                  <div className='flex justify-end gap-3'>
                    <Button type='button' variant='text' onClick={resetFolderForm}>Cancelar</Button>
                    <Button type='submit' loading={isCreatingFolder || isUpdatingFolder}>Guardar</Button>
                  </div>
                </form>
              )}
            </div>
          )}

          <div className='border border-gray-200 bg-white min-w-0 overflow-hidden'>
            <div className='flex items-center justify-between gap-4 p-4 border-b border-gray-200'>
              <Typography variant='h6'>{selectedFolderLabel(selectedFolderId, folders)}</Typography>
              {isFetching && <span className='text-sm text-gray-500'>Actualizando...</span>}
            </div>

            {hasItems
              ? (
                <Table
                  data={nodes}
                  pagination={pagination}
                  className='h-max'
                  layout={{ custom: true, fixedHeader: true }}
                  theme={theme}
                >
                  {(tableList) => (
                    <>
                      <Header>
                        <HeaderRow>
                          <HeaderCell>Nombre</HeaderCell>
                          {showFolderColumn && <HeaderCell>Carpeta</HeaderCell>}
                          <HeaderCell>Info</HeaderCell>
                          <HeaderCell pinRight>Acciones</HeaderCell>
                        </HeaderRow>
                      </Header>
                      <Body>
                        {tableList.map((item) => {
                          const documentUrl = itemDocumentUrl(item)
                          const externalUrl = itemExternalUrl(item)
                          const tooltipDescription = truncateText(item.description)

                          return (
                            <Row key={item.id} item={item}>
                              <Cell>
                                <div className='flex items-center gap-2 min-w-0'>
                                  <FiFileText className='shrink-0' />
                                  <span className='block font-medium whitespace-normal break-words leading-5 min-w-0' style={{ overflowWrap: 'anywhere' }}>
                                    {item.title}
                                  </span>
                                </div>
                              </Cell>
                              {showFolderColumn && <Cell>{item.folder?.name || 'Sin carpeta'}</Cell>}
                              <Cell>
                                <Tooltip content={tooltipDescription} placement='left' className='max-w-xs whitespace-normal'>
                                  <button type='button' aria-label='Ver información' className='text-gray-500 hover:text-blue-600' onClick={() => setDetailsItem(item)}>
                                    <FiInfo />
                                  </button>
                                </Tooltip>
                              </Cell>
                              <Cell pinRight>
                                <div className='flex items-center gap-3'>
                                  {documentUrl && (
                                    <a href={documentUrl} target='_blank' rel='noreferrer' aria-label='Descargar archivo'>
                                      <FiDownload />
                                    </a>
                                  )}
                                  {externalUrl && (
                                    <a href={externalUrl} target='_blank' rel='noreferrer' aria-label='Abrir enlace'>
                                      <FiExternalLink />
                                    </a>
                                  )}
                                  {canManage && (
                                    <>
                                      <button type='button' aria-label='Editar documento' onClick={() => openItemForm(item)}>
                                        <FiEdit2 />
                                      </button>
                                      <button type='button' aria-label='Eliminar documento' onClick={() => removeItem(item)}>
                                        <FiTrash2 />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </Cell>
                            </Row>
                          )
                        })}
                      </Body>
                    </>
                  )}
                </Table>
                )
              : (
                <div className='p-10 text-center text-sm text-gray-500'>
                  No hay documentos publicados en esta carpeta.
                </div>
                )}
          </div>

          {totalPages > 1 && (
            <Pagination
              onPageChange={({ selected }) => pagination.fns.onSetPage(selected)}
              page={pagination.state.page}
              totalPage={totalPages}
            />
          )}
        </section>
        </div>
      </PageLayout>
    </>
  )
}
